import React, {useEffect, useState} from 'react';
import {Label} from '../../models/Label';
import {LabelTab} from './LabelTab';
import {
    ActionButton,
    ContentWrapper,
    DialogEditActionsWrapper,
    TabButton,
    TabNavigation,
    TabNavigationWrapper
} from './LabelTabView.style';
import {t} from 'i18next';
import {
    deleteLabelEntryMap,
    getLabel,
    getLabelEntryMapDefault,
    getLabelMap,
    getLabelMapKeys,
    getLabelNative,
    spreadLabelEntryMap,
    updateLabelEntryMap
} from '../../utils/StorageObjectUtils';
import {generateIdentifier} from '../../utils/IdentityUtils';
import {getTab} from '../../utils/TabUtils';
import {TemplatePropertySettings} from '../../models/TemplatePropertySettings';
import {isVisibleComponent} from '../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_OBJECT_LABELS,
    STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION,
    STORAGE_OBJECT_OBJECT_LABELS_NAME,
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_VERSION
} from '../../init/storage_object_constants';
import {getFontSize} from '../../utils';

export interface LabelTabViewProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly defaultLocale: string;
    readonly labels: Map<string, Label>;
    readonly version: string;
    readonly parentLocales: string[];
    onChangeLabels: (labels: Map<string, Label>) => void;
    onChangeVersion: (version: string) => void;
    onAddAllLabels: () => void;
    onReset: () => void;
}

/**
 * @name LabelTabView
 * @description
 * Component wrapper per StorageObject Label Tabs
 */
export const LabelTabView: React.VFC<LabelTabViewProps> = (props) => {
    let labelTabs = getLabelMapKeys(props.defaultLocale, props.labels);
    const [activeTab, setActiveTab] = useState(getTab(props.defaultLocale, labelTabs));
    const [labelsInput, setLabelsInput] = useState<Map<string, Label>>(getLabelEntryMapDefault(props.defaultLocale, props.labels));
    const [labelInput, setLabelInput] = useState<Label>(getLabelNative(props.defaultLocale, props.labels));
    const [resetStatus, setResetStatus] = useState<boolean>(false);

    const updateActiveTab = (labels: Map<string, Label>): string => {
        labelTabs = getLabelMapKeys(props.defaultLocale, labels);
        let tab = getTab(activeTab, labelTabs);
        setActiveTab(tab);

        return tab;
    }

    const resetTab = (labels: Map<string, Label>) => {
        if (resetStatus) {
            setResetStatus(!resetStatus);
        }

        let tab = updateActiveTab(labels);
        setLabelInput(getLabel(tab, labels));
    };

    useEffect(() => {
        let labels = getLabelMap(props.defaultLocale, props.labels);
        if (resetStatus) {
            resetTab(labels);
        } else {
            updateActiveTab(labels);
        }

        setLabelsInput(labels);
    }, [props.defaultLocale, props.labels]);

    useEffect(() => {
        setLabelInput(getLabelNative(activeTab, labelsInput));
    }, [activeTab, labelsInput]);

    const handleTabClick = (tab: React.SetStateAction<string>) => {
        setActiveTab(tab);
        setLabelInput(getLabelNative(tab.toString(), labelsInput));
    };

    const handleChangeLabel = (label: Label) => {
        let labels = updateLabelEntryMap(activeTab, label, labelsInput);
        props.onChangeLabels(labels);
    };

    const handleChangeVersion = (version: string) => {
        props.onChangeVersion(version);
    };

    const handleSpreadLabels = () => {
        let labels = spreadLabelEntryMap(activeTab, labelInput, labelsInput);
        props.onChangeLabels(labels);
    };

    const handleDeleteLabel = () => {
        let labels = deleteLabelEntryMap(activeTab, labelsInput);
        props.onChangeLabels(labels);
        resetTab(labels);
    };

    const handleAddAllLabels = () => {
        props.onAddAllLabels();
    };

    const handleReset = () => {
        props.onReset();
        setResetStatus(true);
    };

    const getDisableAddAllLabels = (): boolean => {
        let labelKeys = Object.keys(Object.fromEntries(labelsInput));
        let locales = props.parentLocales.filter(locale => (!labelKeys.includes(locale)));

        return (locales.length === 0);
    }

    let fontSize = getFontSize([
        t('TAG_SELECTION_COMPONENT_SPREAD_LABEL'),
        t('TAG_SELECTION_COMPONENT_DELETE_LABEL'),
        t('TAG_SELECTION_COMPONENT_ADD_ALL_LABEL'),
        t('TAG_SELECTION_COMPONENT_RESET_SELECTION_LABEL')
    ], 2);

    return (
        ((isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, props.templatePropertySettings)
                && (isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, props.templatePropertySettings)
                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, props.templatePropertySettings)))
            || (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_VERSION, props.templatePropertySettings))) ?
            <ContentWrapper id={generateIdentifier()}>
                <TabNavigationWrapper id={generateIdentifier()} className='a-tab-navigation__wrapper'>
                    <div id={generateIdentifier()} className='a-tab-navigation__gradients'>
                        <TabNavigation id={generateIdentifier()} className='a-tab-navigation' role='tablist'>
                            {labelTabs?.map((tab, index) => (
                                <li id={generateIdentifier(`'label-item-'${index}`, '-')}
                                    key={`'label-item-'${index}`}
                                    className='a-tab-navigation__item'
                                    role='none'>
                                    <TabButton id={generateIdentifier(`'label-tab-'${tab}`, '-')}
                                               type='button'
                                               className={`a-tab-navigation__tab ${activeTab === tab ? '-selected' : ''}`}
                                               data-frok-tab-identifier={`${index + 1}`}
                                               tabIndex={index}
                                               role='tab'
                                               aria-controls={`'label-tab-'${tab}`}
                                               onClick={() => handleTabClick(tab)}>
                                    <span id={generateIdentifier()} className='a-tab-navigation__tab-content'>
                                        <span id={generateIdentifier()} className='a-tab-navigation__label'>{tab}</span>
                                    </span>
                                    </TabButton>
                                </li>
                            ))}
                        </TabNavigation>
                        <LabelTab templatePropertySettings={props.templatePropertySettings}
                                  locale={activeTab}
                                  label={labelInput}
                                  version={props.version}
                                  onChangeLabel={handleChangeLabel}
                                  onChangeVersion={handleChangeVersion}/>
                    </div>
                </TabNavigationWrapper>
                <DialogEditActionsWrapper id={generateIdentifier()}>
                    <ActionButton id={generateIdentifier()}
                                  key='spread'
                                  icon='copy-paste'
                                  mode='secondary'
                                  disabled={labelTabs.length <= 1}
                                  type='button'
                                  label={t('TAG_SELECTION_COMPONENT_SPREAD_LABEL')}
                                  title={t('TAG_SELECTION_COMPONENT_SPREAD_LABEL')}
                                  fontSize={fontSize}
                                  onClick={handleSpreadLabels}/>
                    <ActionButton id={generateIdentifier()}
                                  key='delete'
                                  icon='delete'
                                  mode='secondary'
                                  disabled={labelTabs.length <= 1}
                                  type='button'
                                  label={t('TAG_SELECTION_COMPONENT_DELETE_LABEL')}
                                  title={t('TAG_SELECTION_COMPONENT_DELETE_LABEL')}
                                  fontSize={fontSize}
                                  onClick={handleDeleteLabel}/>
                    <ActionButton id={generateIdentifier()}
                                  key='addAll'
                                  icon='add'
                                  mode='secondary'
                                  disabled={getDisableAddAllLabels()}
                                  type='button'
                                  label={t('TAG_SELECTION_COMPONENT_ADD_ALL_LABEL')}
                                  title={t('TAG_SELECTION_COMPONENT_ADD_ALL_LABEL')}
                                  fontSize={fontSize}
                                  onClick={handleAddAllLabels}/>
                    <ActionButton id={generateIdentifier()}
                                  key='reset'
                                  icon='reset'
                                  mode='secondary'
                                  disabled={false}
                                  type='button'
                                  label={t('TAG_SELECTION_COMPONENT_RESET_SELECTION_LABEL')}
                                  title={t('TAG_SELECTION_COMPONENT_RESET_SELECTION_LABEL')}
                                  fontSize={fontSize}
                                  onClick={handleReset}/>
                </DialogEditActionsWrapper>
            </ContentWrapper> : <ContentWrapper id={generateIdentifier()}/>
    );
};
